import {
  enforceConsent,
  readConsent,
  resetConsentCache,
  subscribeConsent,
  type ConsentDecision,
} from '@redbrickmedia/duplex.user-identity'
import { isDebugEnabled } from '../debug/isDebugEnabled'
import debug from '../console'
import { markConsentTiming, measureConsentTiming } from './consentTiming'
import {
  ensureUsOptOutSignal,
  resetUsOptOutSignalForTests,
} from './usOptOutSignal'

const logger = debug.withTag('utils:consent')

let countryHint: string | undefined
let pendingCountry: Promise<string | undefined> | undefined
let resolveCountryHint: ((country: string | undefined) => void) | undefined

export function consentCountry(): string | Promise<string | undefined> {
  if (countryHint !== undefined) return countryHint
  pendingCountry ??= new Promise((resolve) => {
    resolveCountryHint = resolve
  })
  return pendingCountry
}

let pending: Promise<ConsentDecision> | null = null
// How the first start went: the package keeps the options its machine started
// with, so later starts change nothing.
let started: { withCountry: boolean } | null = null
let current: ConsentDecision | null = null
let stopPackageSubscription: (() => void) | null = null
const subscribers = new Set<(decision: ConsentDecision) => void>()
// The decisions the gate made up itself because the package failed, so the
// first ad request can tell them from one the package reached.
const appFallbacks = new WeakSet<ConsentDecision>()

function publish(decision: ConsentDecision): void {
  if (current === decision) return

  current = decision
  markConsentTiming(`decision-${decision.state}`)
  logger.debug(
    `Consent resolved: ${decision.state} (policy: ${decision.policy}, evidence: ${decision.evidence.join(',') || 'none'})`
  )
  subscribers.forEach((subscriber) => {
    try {
      subscriber(decision)
    } catch (error) {
      logger.warn('Consent subscriber failed', error)
    }
  })
}

function unknownErrorDecision(): ConsentDecision {
  const decision: ConsentDecision = {
    state: 'unknown',
    policy: 'unknown',
    policies: ['unknown'],
    reason: 'error',
    evidence: ['error'],
    identity: 'unknown',
    firstPartyId: 'unknown',
    restrictions: {
      restrictDataProcessing: true,
      nonPersonalizedAds: true,
    },
    revisable: true,
  }
  appFallbacks.add(decision)
  return decision
}

function settle(decision: ConsentDecision): ConsentDecision {
  publish(decision)
  markConsentTiming('consent-read-settled')
  measureConsentTiming(
    'consent-read',
    'consent-read-start',
    'consent-read-settled'
  )
  return decision
}

function settleAfterFailure(message: string, error: unknown): ConsentDecision {
  logger.warn(`${message}; preserving unknown state`, error)
  const decision = unknownErrorDecision()
  enforceConsent(decision, isDebugEnabled())
  return settle(decision)
}

export function resolveConsent(country?: string): Promise<ConsentDecision> {
  countryHint ??= country
  if (countryHint !== undefined) resolveCountryHint?.(countryHint)
  const options = {
    country,
    debug: isDebugEnabled(),
    // What this allows and what overrides it: the package's DESIGN.md.
    usDefaultAllow: true,
  }

  if (!pending) {
    started = { withCountry: country !== undefined }
    markConsentTiming('consent-read-start')
    try {
      ensureUsOptOutSignal()
      stopPackageSubscription ??= subscribeConsent(publish, options)
      markConsentTiming('consent-api-read-start')
      pending = readConsent(options)
        .then(settle)
        .catch((error) => settleAfterFailure('Consent read failed', error))
    } catch (error) {
      pending = Promise.resolve(
        settleAfterFailure('Consent initialization failed', error)
      )
    }
  }

  return pending
}

export function subscribeToConsent(
  subscriber: (decision: ConsentDecision) => void,
  country?: string
): () => void {
  subscribers.add(subscriber)
  if (current) {
    try {
      subscriber(current)
    } catch (error) {
      logger.warn('Consent subscriber failed', error)
    }
  }
  void resolveConsent(country)

  return () => subscribers.delete(subscriber)
}

/** The decision in effect once consent settled, a later revision included. */
export async function settledConsentDecision(): Promise<ConsentDecision> {
  const settled = await resolveConsent()
  return current ?? settled
}

export function consentSnapshot(): ConsentDecision | null {
  return current
}

/**
 * Where the current decision came from: `package` when user-identity reached
 * it, `app_fallback` when the gate stood in with its own unknown decision
 * after the package failed, `none` before any decision.
 */
export function consentDecisionOrigin(): 'package' | 'app_fallback' | 'none' {
  if (!current) return 'none'
  return appFallbacks.has(current) ? 'app_fallback' : 'package'
}

/**
 * Where the country consent started with came from: `filters` when the filters
 * response gave one, `fallback` when it started without one, `none` before it
 * started.
 */
export function consentCountrySource(): 'filters' | 'fallback' | 'none' {
  if (!started) return 'none'
  return started.withCountry ? 'filters' : 'fallback'
}

export function resetConsentGateForTests(): void {
  stopPackageSubscription?.()
  stopPackageSubscription = null
  pending = null
  started = null
  countryHint = undefined
  pendingCountry = undefined
  resolveCountryHint = undefined
  current = null
  subscribers.clear()
  resetConsentCache()
  resetUsOptOutSignalForTests()
}

export type { ConsentDecision }
